import React, { createContext, useMemo, type ReactNode } from "react";
import { COVERAGE_TYPE_ORDER, CoverageTypeEnum } from "@platform/types";

import { isCoverageTypeEnum } from "../utils";
import { DocumentLibraryCoverageType } from "../coverage-type";
import { usePlanBuilderData } from "../hooks/usePlanBuilderData";

import type { CoverageGroup } from "../types";

export interface CoverageSummary {
  coverageType: DocumentLibraryCoverageType;
  isCurrent: boolean;
  isComplete: boolean;
  isExcluded: boolean;
  alternatePlanCount: number;
}

export interface UseCoverageSummaryReturn {
  coverages: CoverageSummary[];
  excludedCoverages: CoverageTypeEnum[];
  isLoading: boolean;
  isError: boolean;
}

export const CoverageSummaryContext =
  createContext<UseCoverageSummaryReturn | null>(null);

function toCoverageSummary(group: CoverageGroup): CoverageSummary | null {
  if (!isCoverageTypeEnum(group.name)) {
    return null;
  }

  return {
    coverageType: group.name,
    isCurrent: group.isCurrent,
    isComplete: group.status?.key === "complete",
    isExcluded: group.isExcluded,
    alternatePlanCount: group.alternativePlans,
  };
}

type CoverageSummaryProviderProps = {
  rfpId: string;
  children: ReactNode;
};

export const CoverageSummaryProvider = ({
  rfpId,
  children,
}: CoverageSummaryProviderProps): React.JSX.Element => {
  const { isLoading, isError, coverageGroups, sdlCoverage } =
    usePlanBuilderData(rfpId);

  const coverages = useMemo<CoverageSummary[]>(() => {
    const entries = coverageGroups.flatMap((group) => {
      const summary = toCoverageSummary(group);
      return summary ? [summary] : [];
    });

    if (sdlCoverage) {
      entries.push({
        coverageType: DocumentLibraryCoverageType.STATE_DISABILITY_AND_LEAVES,
        isCurrent: sdlCoverage.isCurrent,
        isComplete: false,
        isExcluded: false,
        alternatePlanCount: 0,
      });
    }

    return entries.sort((a, b) => {
      const aOrder =
        COVERAGE_TYPE_ORDER[a.coverageType as CoverageTypeEnum] ??
        Number.MAX_SAFE_INTEGER;
      const bOrder =
        COVERAGE_TYPE_ORDER[b.coverageType as CoverageTypeEnum] ??
        Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
  }, [coverageGroups, sdlCoverage]);

  // The BFF decides which coverage types are excluded — it gates the list
  // behind the ai-dox-extended-coverages flag — so read its verdict off each
  // group rather than re-deriving it from a client-side constant.
  // isCurrent is checked here as well: AI processing only runs on current
  // coverages, and the composer leaves groups that exist only in AI Dox
  // (unmatchedAIDoxAsGroups) marked excluded despite always being new.
  const excludedCoverages = useMemo(
    () =>
      coverages.flatMap((coverage) =>
        coverage.isExcluded &&
        coverage.isCurrent &&
        isCoverageTypeEnum(coverage.coverageType)
          ? [coverage.coverageType]
          : [],
      ),
    [coverages],
  );

  const value = useMemo<UseCoverageSummaryReturn>(
    () => ({
      coverages,
      excludedCoverages,
      isLoading,
      isError,
    }),
    [coverages, excludedCoverages, isLoading, isError],
  );

  return (
    <CoverageSummaryContext.Provider value={value}>
      {children}
    </CoverageSummaryContext.Provider>
  );
};
